import React from 'react';
import {
  AlertTriangle,
  BarChart3,
  Building2,
  ClipboardCheck,
  FlaskConical,
  Gauge,
  KeyRound,
  LayoutDashboard,
  Layers,
  PlayCircle,
  Rocket,
  Shield,
  Target,
  TrendingUp,
  UserCircle2,
  Users,
  Workflow,
} from 'lucide-react';
import type { Capability, CapabilityTarget } from '../../permissions';
import { hasCapability } from '../../permissions';
import { OperacoesPage } from '../../pages/OperacoesPage';
import { WorkflowPage } from '../../pages/WorkflowPage';
import { GanhosPage } from '../../pages/GanhosPage';
import { DashboardPage } from '../../pages/DashboardPage';
import { MapaPage } from '../../pages/MapaPage';
import { AcoesPage } from '../../pages/AcoesPage';
import { ProjectsPage } from '../../pages/ProjectsPage';
import { ProjectHubPage } from '../../pages/ProjectHubPage';
import { ExcelenciaDashboard } from '../../modules/excelencia/pages';
import {
  AccountPreferencesPage,
  AccountProfilePage,
  AccountSecurityPage,
} from '../../modules/account/pages';
import { BillingPage, CheckoutPage, OrganizationPage, OrgStructurePage } from '../../modules/org/pages';
import {
  AdminApiKeysPage,
  AdminAuditLogsPage,
  AdminAutomationsPage,
  AdminBillingPage,
  AdminClientDetailPage,
  AdminClientsPage,
  AdminDashboardPage,
  AdminDepartmentsPage,
  AdminDeveloperPortalPage,
  AdminEventBusPage,
  AdminHierarchyPage,
  AdminIntegracoesConsolidatedPage,
  AdminIntegrationsPage,
  AdminLicencasConsolidatedPage,
  AdminLicensesPage,
  AdminMarketplacePage,
  AdminOrganizationPage,
  AdminOverviewPage,
  AdminPermissionsPage,
  AdminPessoasPage,
  AdminPlataformaPage,
  AdminGovernancaTemposPage,
  AdminSecurityPage,
  AdminSegurancaConsolidatedPage,
  AdminSistemaPage,
  AdminTemplatesPage,
  AdminUsersPage,
  AdminRolesPage,
} from '../../modules/admin/pages';
import { SimuladorPage } from '../../pages/SimuladorPage';
import { ProblemasPage } from '../../pages/ProblemasPage';
import { LegacyExecutionRedirectPage } from '../../modules/execution/pages/LegacyExecutionRedirectPage';
import { MinhaMesaPage } from '../../modules/minha-mesa/pages';
import { WorkbenchPage } from '../../pages/WorkbenchPage';
import { TeamPage } from '../../pages/TeamPage';
import { OperationsCenterPage } from '../../pages/OperationsCenterPage';
import { CentralHoshinPage } from '../../pages/CentralHoshinPage';
import { CentralFluxosPage } from '../../pages/CentralFluxosPage';
import { CockpitDiretoriaPage } from '../../pages/CockpitDiretoriaPage';
import { PrimeiroUsoWizard } from '../../modules/primeiro-uso';

export interface NavigationRouteMeta {
  title: string;
  immersive: boolean;
  breadcrumb: boolean;
  hasRibbon: boolean;
}

export interface NavigationSectionConfig {
  id: string;
  label: string;
  collapsible?: boolean;
}

export interface NavigationItemConfig {
  id: string;
  label: string;
  path: string;
  matchers: string[];
  icon: React.ReactNode;
  sectionId?: string;
  sidebar: boolean;
  end?: boolean;
  capability?: Capability;
  realRoleOnly?: boolean;
  routeMeta: NavigationRouteMeta;
}

export interface ProtectedRouteDefinition {
  id: string;
  path: string;
  component?: React.ComponentType;
  redirectTo?: string;
  capability?: Capability;
  realRoleOnly?: boolean;
}

export interface SidebarSectionGroup {
  section: NavigationSectionConfig;
  items: NavigationItemConfig[];
}

const SECTION_ORDER: NavigationSectionConfig[] = [
  { id: 'operacao', label: 'Operação' },
  { id: 'gestao_metodo', label: 'Gestão & Método' },
  { id: 'estrategia', label: 'Estratégia' },
  { id: 'institucional', label: 'Configuração' },
];

const NAVIGATION_ITEMS: NavigationItemConfig[] = [
  {
    id: 'cockpit-diretoria',
    label: 'Cockpit Diretoria',
    path: '/cockpit-diretoria',
    matchers: ['/cockpit-diretoria'],
    icon: <LayoutDashboard size={16} />,
    sectionId: 'estrategia',
    sidebar: true,
    end: true,
    routeMeta: { title: 'Cockpit Diretoria', immersive: false, breadcrumb: false, hasRibbon: false },
  },
  {
    id: 'centro-operacional',
    label: 'Centro Operacional',
    path: '/centro-operacional',
    matchers: ['/centro-operacional'],
    icon: <Gauge size={16} />,
    sectionId: 'operacao',
    sidebar: true,
    end: true,
    routeMeta: { title: 'Centro Operacional', immersive: false, breadcrumb: false, hasRibbon: true },
  },
  {
    id: 'mesa',
    label: 'Minhas Tarefas',
    path: '/minha-mesa',
    matchers: ['/minha-mesa', '/mesa'],
    icon: <ClipboardCheck size={16} />,
    sectionId: 'operacao',
    sidebar: true,
    routeMeta: { title: 'Minhas Tarefas', immersive: false, breadcrumb: true, hasRibbon: true },
  },
  {
    id: 'execucoes',
    label: 'Workflows',
    path: '/execucoes',
    matchers: ['/execucoes', '/workflow'],
    icon: <PlayCircle size={16} />,
    sectionId: 'operacao',
    sidebar: true,
    routeMeta: { title: 'Workflows', immersive: false, breadcrumb: true, hasRibbon: true },
  },
  {
    id: 'gestao',
    label: 'Gestão (Kanban)',
    path: '/gestao',
    matchers: ['/gestao'],
    icon: <LayoutDashboard size={16} />,
    sidebar: false,
    routeMeta: { title: 'Gestão', immersive: false, breadcrumb: true, hasRibbon: false },
  },
  {
    id: 'ganhos',
    label: 'Monitor de Ganhos',
    path: '/ganhos',
    matchers: ['/ganhos'],
    icon: <TrendingUp size={16} />,
    sidebar: false,
    routeMeta: { title: 'Monitor de Ganhos', immersive: false, breadcrumb: true, hasRibbon: false },
  },
  {
    id: 'analytics',
    label: 'Analytics',
    path: '/analytics',
    matchers: ['/analytics', '/dashboard'],
    icon: <BarChart3 size={16} />,
    sidebar: false,
    routeMeta: { title: 'Analytics', immersive: false, breadcrumb: true, hasRibbon: false },
  },
  {
    id: 'mapa',
    label: 'Mapa de Fluxos',
    path: '/mapa',
    matchers: ['/mapa'],
    icon: <Workflow size={16} />,
    sectionId: 'gestao_metodo',
    sidebar: true,
    routeMeta: { title: 'Mapa de Fluxos', immersive: false, breadcrumb: false, hasRibbon: true },
  },
  {
    id: 'fluxos',
    label: 'Central de Fluxos',
    path: '/fluxos',
    matchers: ['/fluxos'],
    icon: <Layers size={16} />,
    sectionId: 'gestao_metodo',
    sidebar: true,
    end: true,
    routeMeta: { title: 'Central de Fluxos', immersive: false, breadcrumb: false, hasRibbon: true },
  },
  {
    id: 'excelencia',
    label: 'Blueprint',
    path: '/excelencia',
    matchers: ['/excelencia'],
    icon: <Shield size={16} />,
    sectionId: 'gestao_metodo',
    sidebar: true,
    routeMeta: { title: 'Blueprint', immersive: false, breadcrumb: false, hasRibbon: true },
  },
  {
    id: 'problemas',
    label: 'Problemas',
    path: '/problemas',
    matchers: ['/problemas'],
    icon: <AlertTriangle size={16} />,
    sectionId: 'gestao_metodo',
    sidebar: true,
    routeMeta: { title: 'Problemas', immersive: false, breadcrumb: false, hasRibbon: true },
  },
  {
    id: 'acoes',
    label: 'Central de Ações',
    path: '/acoes',
    matchers: ['/acoes', '/plano'],
    icon: <Target size={16} />,
    sectionId: 'gestao_metodo',
    sidebar: true,
    routeMeta: { title: 'Central de Ações', immersive: false, breadcrumb: false, hasRibbon: true },
  },
  {
    id: 'projetos',
    label: 'Projetos',
    path: '/projetos',
    matchers: ['/projetos'],
    icon: <Building2 size={16} />,
    sectionId: 'estrategia',
    sidebar: true,
    routeMeta: { title: 'Projetos', immersive: false, breadcrumb: false, hasRibbon: true },
  },
  {
    id: 'estrategia-hoshin',
    label: 'Hoshin Kanri',
    path: '/estrategia',
    matchers: ['/estrategia'],
    icon: <TrendingUp size={16} />,
    sectionId: 'estrategia',
    sidebar: true,
    end: true,
    routeMeta: { title: 'Desdobramento Estratégico', immersive: false, breadcrumb: false, hasRibbon: false },
  },
  {
    id: 'operacoes',
    label: 'Análise',
    path: '/operacoes',
    matchers: ['/operacoes'],
    icon: <BarChart3 size={16} />,
    sectionId: 'estrategia',
    sidebar: true,
    routeMeta: { title: 'Análise', immersive: false, breadcrumb: true, hasRibbon: false },
  },
  {
    id: 'simulador',
    label: 'Simulação',
    path: '/simulador',
    matchers: ['/simulador'],
    icon: <FlaskConical size={16} />,
    sectionId: 'estrategia',
    sidebar: false, // integrado ao módulo Capacidade (/equipe) via Ribbon → "Simular Cenário"
    routeMeta: { title: 'Simulação', immersive: false, breadcrumb: false, hasRibbon: true },
  },
  {
    id: 'equipe',
    label: 'Capacidade',
    path: '/equipe',
    matchers: ['/equipe'],
    icon: <Users size={16} />,
    sectionId: 'estrategia',
    sidebar: true,
    routeMeta: { title: 'Capacidade & FTE', immersive: false, breadcrumb: false, hasRibbon: true },
  },
  {
    id: 'org',
    label: 'Organização',
    path: '/org',
    matchers: ['/org', '/org/billing', '/org/estrutura'],
    icon: <Building2 size={16} />,
    sectionId: 'institucional',
    sidebar: true,
    capability: 'admin.access',
    realRoleOnly: true,
    routeMeta: { title: 'Organização', immersive: false, breadcrumb: false, hasRibbon: false },
  },
  {
    id: 'admin-overview',
    label: 'Administração',
    path: '/administracao',
    matchers: [
      '/administracao',
      '/administracao/dashboard',
      '/administracao/organizacao',
      '/administracao/pessoas',
      '/administracao/usuarios',
      '/administracao/departamentos',
      '/administracao/hierarquia',
      '/administracao/cargos',
      '/administracao/clientes',
      '/administracao/licencas',
      '/administracao/billing',
      '/administracao/seguranca',
      '/administracao/permissoes',
      '/administracao/api-keys',
      '/administracao/integracoes',
      '/administracao/integracao',
      '/administracao/auditoria',
      '/administracao/templates',
      '/administracao/sistema',
      '/administracao/plataforma',
      '/administracao/marketplace',
      '/administracao/automacoes',
      '/administracao/event-bus',
      '/administracao/developers',
      '/administracao/governanca-tempos',
    ],
    icon: <Shield size={15} />,
    sectionId: 'institucional',
    sidebar: true,
    capability: 'admin.access',
    realRoleOnly: true,
    routeMeta: { title: 'Administração', immersive: false, breadcrumb: false, hasRibbon: true },
  },
  {
    id: 'account',
    label: 'Conta',
    path: '/account',
    matchers: ['/account', '/account/profile', '/account/preferences', '/account/security'],
    icon: <UserCircle2 size={16} />,
    sectionId: 'institucional',
    sidebar: true,
    routeMeta: { title: 'Conta', immersive: false, breadcrumb: false, hasRibbon: false },
  },
  {
    id: 'admin-users',
    label: 'Usuários',
    path: '/administracao/usuarios',
    matchers: ['/administracao/usuarios'],
    icon: <Users size={15} />,
    sidebar: false,
    capability: 'admin.access',
    realRoleOnly: true,
    routeMeta: { title: 'Administração', immersive: false, breadcrumb: false, hasRibbon: false },
  },
  {
    id: 'admin-clients',
    label: 'Clientes',
    path: '/administracao/clientes',
    matchers: ['/administracao/clientes'],
    icon: <Building2 size={15} />,
    sidebar: false,
    capability: 'admin.access',
    realRoleOnly: true,
    routeMeta: { title: 'Administração', immersive: false, breadcrumb: false, hasRibbon: false },
  },
  {
    id: 'admin-api-keys',
    label: 'API',
    path: '/administracao/api-keys',
    matchers: ['/administracao/api-keys'],
    icon: <KeyRound size={15} />,
    sidebar: false,
    capability: 'admin.access',
    realRoleOnly: true,
    routeMeta: { title: 'Administração', immersive: false, breadcrumb: false, hasRibbon: false },
  },
  {
    id: 'primeiro-uso',
    label: 'Primeiro Uso',
    path: '/primeiro-uso',
    matchers: ['/primeiro-uso'],
    icon: <Rocket size={16} />,
    sidebar: false,
    routeMeta: { title: 'Primeiro Uso', immersive: true, breadcrumb: false, hasRibbon: false },
  },
];

const PROTECTED_ROUTE_DEFINITIONS: ProtectedRouteDefinition[] = [
  { id: 'home-redirect', path: '/home', redirectTo: '/centro-operacional' },
  { id: 'primeiro-uso', path: '/primeiro-uso', component: PrimeiroUsoWizard },
  { id: 'cockpit-diretoria', path: '/cockpit-diretoria', component: CockpitDiretoriaPage },
  { id: 'centro-operacional', path: '/centro-operacional', component: OperationsCenterPage },
  { id: 'operacoes', path: '/operacoes', component: OperacoesPage },
  { id: 'execucoes', path: '/execucoes/*', component: WorkflowPage },
  { id: 'workflow-redirect', path: '/workflow', component: () => <LegacyExecutionRedirectPage target="instancias" /> },
  { id: 'workflow-wildcard-redirect', path: '/workflow/*', component: () => <LegacyExecutionRedirectPage target="instancias" /> },
  { id: 'gestao-redirect', path: '/gestao', component: () => <LegacyExecutionRedirectPage target="kanban" /> },
  { id: 'gestao-wildcard-redirect', path: '/gestao/*', component: () => <LegacyExecutionRedirectPage target="kanban" /> },
  { id: 'entradas-redirect', path: '/entradas', redirectTo: '/minha-mesa' },
  { id: 'nova-execucao-redirect', path: '/execucoes/nova/:blueprintId', redirectTo: '/minha-mesa' },
  { id: 'minha-mesa', path: '/minha-mesa', component: MinhaMesaPage },
  { id: 'minha-mesa-wildcard-redirect', path: '/minha-mesa/*', redirectTo: '/minha-mesa' },
  { id: 'mesa-redirect', path: '/mesa', redirectTo: '/minha-mesa' },
  { id: 'mesa-wildcard-redirect', path: '/mesa/*', redirectTo: '/minha-mesa' },
  { id: 'ganhos', path: '/ganhos', component: GanhosPage },
  { id: 'analytics', path: '/analytics', component: DashboardPage },
  { id: 'analytics-redirect', path: '/dashboard', redirectTo: '/analytics' },
  { id: 'mapa', path: '/mapa', component: MapaPage },
  { id: 'plano', path: '/plano', redirectTo: '/acoes' },
  { id: 'plano-wildcard', path: '/plano/*', redirectTo: '/acoes' },
  { id: 'acoes', path: '/acoes', component: AcoesPage },
  { id: 'acoes-lista', path: '/acoes/lista', component: AcoesPage },
  { id: 'acoes-nova', path: '/acoes/nova', component: AcoesPage },
  { id: 'acoes-kanban', path: '/acoes/kanban', component: AcoesPage },
  { id: 'acoes-dashboard', path: '/acoes/dashboard', component: AcoesPage },
  { id: 'acoes-matriz', path: '/acoes/matriz', component: AcoesPage },
  { id: 'projetos', path: '/projetos', component: ProjectsPage },
  { id: 'projeto-detail', path: '/projetos/:id', component: ProjectHubPage },
  { id: 'estrategia-hoshin', path: '/estrategia', component: CentralHoshinPage },
  { id: 'fluxos', path: '/fluxos', component: CentralFluxosPage },
  { id: 'excelencia', path: '/excelencia', component: ExcelenciaDashboard, capability: 'workflow.full_access', realRoleOnly: true },
  { id: 'consultoria', path: '/consultoria', component: WorkbenchPage },
  { id: 'problemas', path: '/problemas', component: ProblemasPage },
  { id: 'simulador', path: '/simulador', component: SimuladorPage },
  { id: 'equipe', path: '/equipe', component: TeamPage },
  { id: 'account-profile', path: '/account/profile', component: AccountProfilePage },
  { id: 'account-preferences', path: '/account/preferences', component: AccountPreferencesPage },
  { id: 'account-security', path: '/account/security', component: AccountSecurityPage },
  { id: 'account-redirect', path: '/account', redirectTo: '/account/profile' },
  { id: 'account-wildcard', path: '/account/*', redirectTo: '/account/profile' },
  { id: 'org', path: '/org', component: OrganizationPage },
  {
    id: 'org-billing',
    path: '/org/billing',
    component: BillingPage,
    capability: 'billing.manage',
    realRoleOnly: true,
  },
  {
    id: 'checkout',
    path: '/checkout',
    component: CheckoutPage,
  },
  { id: 'org-estrutura', path: '/org/estrutura', component: OrgStructurePage, capability: 'org_structure.view' },
  { id: 'org-wildcard', path: '/org/*', redirectTo: '/org' },
  {
    id: 'admin-overview',
    path: '/administracao',
    component: AdminOverviewPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  {
    id: 'admin-dashboard',
    path: '/administracao/dashboard',
    component: AdminDashboardPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  {
    id: 'admin-organizacao',
    path: '/administracao/organizacao',
    component: AdminOrganizationPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  // ── Consolidated: Pessoas (Usuários + Deptos + Hierarquia + Cargos) ──
  {
    id: 'admin-pessoas',
    path: '/administracao/pessoas',
    component: AdminPessoasPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  // Legacy individual routes redirect to consolidated
  { id: 'admin-users', path: '/administracao/usuarios', redirectTo: '/administracao/pessoas' },
  { id: 'admin-departamentos', path: '/administracao/departamentos', redirectTo: '/administracao/pessoas' },
  { id: 'admin-hierarquia', path: '/administracao/hierarquia', redirectTo: '/administracao/pessoas' },
  { id: 'admin-cargos', path: '/administracao/cargos', redirectTo: '/administracao/pessoas' },
  {
    id: 'admin-clients',
    path: '/administracao/clientes',
    component: AdminClientsPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  {
    id: 'admin-client-detail',
    path: '/administracao/clientes/:orgId',
    component: AdminClientDetailPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  // ── Consolidated: Licenças + Billing ──
  {
    id: 'admin-licencas',
    path: '/administracao/licencas',
    component: AdminLicencasConsolidatedPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  { id: 'admin-billing', path: '/administracao/billing', redirectTo: '/administracao/licencas' },
  // ── Consolidated: Segurança (Permissões + Segurança) ──
  {
    id: 'admin-seguranca',
    path: '/administracao/seguranca',
    component: AdminSegurancaConsolidatedPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  { id: 'admin-permissoes', path: '/administracao/permissoes', redirectTo: '/administracao/seguranca' },
  {
    id: 'admin-auditoria',
    path: '/administracao/auditoria',
    component: AdminAuditLogsPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  {
    id: 'admin-templates',
    path: '/administracao/templates',
    component: AdminTemplatesPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  {
    id: 'admin-sistema',
    path: '/administracao/sistema',
    component: AdminSistemaPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  // ── Consolidated: Integrações (API Keys + Integrações) ──
  {
    id: 'admin-integracao',
    path: '/administracao/integracao',
    component: AdminIntegracoesConsolidatedPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  { id: 'admin-api-keys', path: '/administracao/api-keys', redirectTo: '/administracao/integracao' },
  { id: 'admin-integracoes', path: '/administracao/integracoes', redirectTo: '/administracao/integracao' },
  // ── Consolidated: Plataforma (Marketplace + Automações + Event Bus + Developers) ──
  {
    id: 'admin-plataforma',
    path: '/administracao/plataforma',
    component: AdminPlataformaPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  { id: 'admin-marketplace', path: '/administracao/marketplace', redirectTo: '/administracao/plataforma' },
  { id: 'admin-automacoes', path: '/administracao/automacoes', redirectTo: '/administracao/plataforma' },
  { id: 'admin-event-bus', path: '/administracao/event-bus', redirectTo: '/administracao/plataforma' },
  { id: 'admin-developers', path: '/administracao/developers', redirectTo: '/administracao/plataforma' },
  // ── Inteligência Temporal ──
  {
    id: 'admin-governanca-tempos',
    path: '/administracao/governanca-tempos',
    component: AdminGovernancaTemposPage,
    capability: 'admin.access',
    realRoleOnly: true,
  },
  { id: 'administracao-wildcard', path: '/administracao/*', redirectTo: '/administracao/dashboard' },
  { id: 'admin-wildcard', path: '/admin/*', redirectTo: '/administracao' },
];

const DEFAULT_ROUTE_META: NavigationRouteMeta = {
  title: 'Workspace',
  immersive: false,
  breadcrumb: false,
  hasRibbon: false,
};

function matchesPath(pathname: string, matcher: string): boolean {
  return pathname === matcher || pathname.startsWith(`${matcher}/`);
}

function canAccessItem(target: CapabilityTarget | null | undefined, item: NavigationItemConfig): boolean {
  if (!item.capability) {
    return true;
  }

  return hasCapability(target, item.capability, { realRoleOnly: item.realRoleOnly });
}

export function getNavigationItemByPath(pathname: string): NavigationItemConfig | null {
  return (
    NAVIGATION_ITEMS.find((item) => item.matchers.some((matcher) => matchesPath(pathname, matcher))) ??
    null
  );
}

export function getNavigationRouteMeta(pathname: string): NavigationRouteMeta {
  return getNavigationItemByPath(pathname)?.routeMeta ?? DEFAULT_ROUTE_META;
}

export function getActiveNavigationSectionId(pathname: string): string | null {
  return getNavigationItemByPath(pathname)?.sectionId ?? null;
}

export function getSidebarNavigation(target: CapabilityTarget | null | undefined): SidebarSectionGroup[] {
  return SECTION_ORDER.map((section) => {
    const items = NAVIGATION_ITEMS.filter(
      (item) => item.sidebar && item.sectionId === section.id && canAccessItem(target, item),
    );

    return {
      section,
      items,
    };
  }).filter((group) => group.items.length > 0);
}

export function getProtectedRoutes(): ProtectedRouteDefinition[] {
  return PROTECTED_ROUTE_DEFINITIONS;
}

export function toNestedRoutePath(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path;
}
